import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import RiskIndicator from '../components/ui/RiskIndicator';
import AttendancePanel from '../components/AttendancePanel';
import DateSelect from '../components/ui/DateSelect';
import TimelineView from '../components/TimelineView';
import Avatar from '../components/ui/Avatar';
import SearchableChecklist from '../components/ui/SearchableChecklist';
import SearchableSelect from '../components/ui/SearchableSelect';
import { useStudentStore } from '../store/StudentStore';
import { useUser } from '../store/UserContext';
import { useSettings } from '../store/SettingsContext';
import Modal from '../components/ui/Modal';
import StaffService from '../services/StaffService';
import ProgrammeService from '../services/ProgrammeService';
import ActivityService from '../services/ActivityService';
import AssignmentService from '../services/AssignmentService';
import ExternalMessageService from '../services/ExternalMessageService';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { readFilesAsStoredAttachments } from '../utils/fileAttachments';

const incidentTypes = ['Injury', 'Safeguarding concern', 'Property damage', 'Theft', 'Behavioural', 'Other'];

const parseDateValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return null;
  return date;
};

export default function StudentDashboardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { controller, incidentController, inventoryController, inventoryItems, timelineService } = useStudentStore();
  const { user } = useUser();
  const { settings } = useSettings();
  const staffService = useMemo(() => new StaffService(), []);
  const programmeService = useMemo(() => new ProgrammeService(), []);
  const activityService = useMemo(() => new ActivityService(), []);
  const assignmentService = useMemo(() => new AssignmentService(), []);
  const externalMessageService = useMemo(() => new ExternalMessageService(), []);
  const staffList = useMemo(() => staffService.list(), [staffService]);
  const [showSafeguardingModal, setShowSafeguardingModal] = useState(false);
  const [eventFiles, setEventFiles] = useState([]);
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [incidentFiles, setIncidentFiles] = useState([]);
  const [incidentInventorySelection, setIncidentInventorySelection] = useState([]);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [newInventoryItem, setNewInventoryItem] = useState({
    name: '',
    type: 'Tech Equipment',
    condition: 'Good'
  });
  const [incidentStaffSelection, setIncidentStaffSelection] = useState([]);
  const [incidentForm, setIncidentForm] = useState({
    type: 'Behavioural',
    description: '',
    date: new Date().toISOString().slice(0, 16),
    severity: 'medium',
    status: 'open',
    assignedKeyWorker: user?.name || '',
    followUpRequired: true,
    followUpDate: '',
    followUpPrompt: ''
  });
  const [incidentError, setIncidentError] = useState('');
  const [safeguardingForm, setSafeguardingForm] = useState({
    type: '',
    severity: 'medium',
    status: 'open',
    description: '',
    date: new Date().toISOString().slice(0, 10),
    assignedOwner: user?.name || '',
    reviewDate: '',
    escalationLevel: 'L1',
    attachments: ''
  });
  const [safeguardingError, setSafeguardingError] = useState('');

  const student = controller.getStudent(id);
  const attendanceThreshold = settings.attendanceThreshold || 70;
  const attendanceFlag = student ? (student.attendance?.rate || 0) < attendanceThreshold : false;

  const timelineEvents = useMemo(() => {
    if (!student) return [];
    const now = new Date();
    const cutoff = new Date();
    cutoff.setDate(now.getDate() - 60);
    return timelineService
      .buildForStudent(student)
      .filter((event) => {
        const date = new Date(event.date);
        return !Number.isNaN(date.valueOf()) && date >= cutoff;
      });
  }, [student, timelineService]);

  const documentAlerts = useMemo(() => {
    const alerts = [];
    if (!student) {
      return { alerts, overdue: 0, dueSoon: 0, expired: 0, expiringSoon: 0 };
    }
    const now = new Date();
    const dueSoonThreshold = new Date();
    dueSoonThreshold.setDate(now.getDate() + 14);
    const expirySoonThreshold = new Date();
    expirySoonThreshold.setDate(now.getDate() + 30);

    let overdue = 0;
    let dueSoon = 0;
    let expired = 0;
    let expiringSoon = 0;

    (student.documents || []).forEach((doc) => {
      if (doc.archived) return;
      const dueValue = doc.dueDate || doc.metadata?.dueDate;
      const dueDate = parseDateValue(dueValue);
      if (dueDate) {
        if (dueDate < now) {
          overdue += 1;
          alerts.push({ id: doc.id, name: doc.name, type: 'Due', status: 'Overdue', date: dueValue });
        } else if (dueDate <= dueSoonThreshold) {
          dueSoon += 1;
          alerts.push({ id: doc.id, name: doc.name, type: 'Due', status: 'Due soon', date: dueValue });
        }
      }

      const expiryValue = doc.expiryDate || doc.metadata?.expiryDate;
      const expiryDate = parseDateValue(expiryValue);
      if (expiryDate) {
        if (expiryDate < now) {
          expired += 1;
          alerts.push({ id: doc.id, name: doc.name, type: 'Expiry', status: 'Expired', date: expiryValue });
        } else if (expiryDate <= expirySoonThreshold) {
          expiringSoon += 1;
          alerts.push({ id: doc.id, name: doc.name, type: 'Expiry', status: 'Expiring soon', date: expiryValue });
        }
      }
    });

    (student.certificates || []).forEach((cert) => {
      if (cert.archived) return;
      const expiryValue = cert.expiryDate;
      const expiryDate = parseDateValue(expiryValue);
      if (!expiryDate) return;
      if (expiryDate < now) {
        expired += 1;
        alerts.push({ id: cert.id, name: cert.name, type: 'Certificate', status: 'Expired', date: expiryValue });
      } else if (expiryDate <= expirySoonThreshold) {
        expiringSoon += 1;
        alerts.push({ id: cert.id, name: cert.name, type: 'Certificate', status: 'Expiring soon', date: expiryValue });
      }
    });

    alerts.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    return { alerts, overdue, dueSoon, expired, expiringSoon };
  }, [student]);

  const activeSafeguardingCount = student?.activeSafeguardingCount || 0;
  const supportWorkerOptions = [
    ...(user?.name ? [{ name: user.name, role: user.role || user.title || '' }] : []),
    ...(student.supportWorkers || []),
    ...(student.keyWorker ? [student.keyWorker] : []),
    ...staffList
  ].reduce((acc, worker) => {
    if (!worker || !worker.name) return acc;
    if (!acc.find((item) => item.name === worker.name)) {
      acc.push(worker);
    }
    return acc;
  }, []);
  const supportWorkerSelectOptions = supportWorkerOptions.map((worker) => ({
    value: worker.name,
    label: worker.name,
    meta: worker.role || worker.title || ''
  }));

  const incidentReports = incidentController.listByStudent(student.id);
  const recentIncidents = [...incidentReports].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 3);

  const assignedItems = inventoryController.getAssignmentsForStudent(student.id);
  const programmes = useMemo(() => programmeService.list(), [programmeService]);
  const activities = useMemo(() => activityService.list(), [activityService]);
  const enrolledProgrammes = useMemo(() => {
    if (!student) return [];
    const programmeNames = new Set((student.enrolments || []).map((enrol) => enrol.programme));
    return programmes.filter((programme) => programmeNames.has(programme.name));
  }, [programmes, student]);
  const enrolledActivities = useMemo(() => {
    if (!student) return [];
    const activityIds = new Set((student.activities || []).map((activity) => activity.activityId || activity.id));
    return activities.filter((activity) => activityIds.has(activity.id));
  }, [activities, student]);
  const lessonPlans = useMemo(() => {
    const plans = [
      ...enrolledProgrammes.flatMap((programme) => (programme.lessonPlans || []).map((plan) => ({
        ...plan,
        source: programme.name
      }))),
      ...enrolledActivities.flatMap((activity) => (activity.lessonPlans || []).map((plan) => ({
        ...plan,
        source: activity.name
      })))
    ];
    return plans.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  }, [enrolledProgrammes, enrolledActivities]);
  const assignments = useMemo(
    () => (student ? assignmentService.listForStudents([student.id]) : []),
    [assignmentService, student]
  );

  const getAssignmentReviewStatus = useCallback((assignment) => {
    if (!student) return '';
    const reviews = (assignment.timeline || [])
      .filter((entry) => entry.type === 'review' && entry.studentId === student.id)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    if (reviews.length === 0) return '';
    return reviews[0].reviewStatus || '';
  }, [student]);

  const assignmentStatusCounts = useMemo(() => {
    const counts = { approved: 0, failed: 0, resubmit: 0, other: 0, pending: 0 };
    assignments.forEach((assignment) => {
      const status = getAssignmentReviewStatus(assignment);
      if (!status) {
        counts.pending += 1;
      } else if (status === 'approved') {
        counts.approved += 1;
      } else if (status === 'failed') {
        counts.failed += 1;
      } else if (status === 'resubmit') {
        counts.resubmit += 1;
      } else {
        counts.other += 1;
      }
    });
    return counts;
  }, [assignments, getAssignmentReviewStatus]);
  const studentMessages = useMemo(
    () => (student ? externalMessageService.listForStudent(student.id) : []),
    [externalMessageService, student]
  );
  const emergencyContacts = useMemo(
    () => (student?.contacts || []).filter((contact) => contact.type === 'guardian'),
    [student]
  );

  if (!student) {
    return (
      <Card className="p-6">
        <p className="sp-muted">Student not found.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/students')}>
          Back to students
        </Button>
      </Card>
    );
  }

  const toggleIncidentInventory = (itemId) => {
    setIncidentInventorySelection((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const toggleIncidentStaff = (staffId) => {
    setIncidentStaffSelection((prev) =>
      prev.includes(staffId) ? prev.filter((id) => id !== staffId) : [...prev, staffId]
    );
  };

  const handleAddInventoryItem = () => {
    if (!newInventoryItem.name.trim()) return;
    const item = inventoryController.createItem(
      {
        name: newInventoryItem.name.trim(),
        type: newInventoryItem.type,
        condition: newInventoryItem.condition,
        status: 'available'
      },
      user
    );
    setIncidentInventorySelection((prev) => [item.id, ...prev]);
    setNewInventoryItem({ name: '', type: 'Tech Equipment', condition: 'Good' });
    setShowInventoryModal(false);
  };

  const handleIncidentSubmit = async () => {
    if (!incidentForm.description.trim()) {
      setIncidentError('Description is required.');
      return;
    }
    let documents = [];
    try {
      documents = await readFilesAsStoredAttachments(incidentFiles, user?.name || 'System', {
        entityType: 'student',
        entityId: student.id,
        entityName: student.name,
        category: 'incidents',
        documentType: incidentForm.type || 'incident'
      });
    } catch (error) {
      setIncidentError(error.message || 'Unable to upload incident attachments.');
      return;
    }
    const participants = [
      { id: student.id, type: 'student', name: student.name, role: 'Student' },
      ...incidentStaffSelection.map((staffId) => {
        const staff = staffList.find((worker) => worker.id === staffId);
        return { id: staffId, type: 'staff', name: staff?.name || '', role: staff?.role || 'Support Worker' };
      })
    ];

    incidentController.createIncident(
      {
        type: incidentForm.type,
        date: incidentForm.date,
        severity: incidentForm.severity,
        status: incidentForm.status,
        reportedBy: user?.name || 'System',
        description: incidentForm.description.trim(),
        assignedKeyWorker: incidentForm.assignedKeyWorker,
        followUpRequired: incidentForm.followUpRequired,
        followUpDate: incidentForm.followUpRequired ? incidentForm.followUpDate : null,
        followUpPrompt: incidentForm.followUpRequired ? incidentForm.followUpPrompt : '',
        participants,
        inventoryItemIds: incidentInventorySelection,
        documents
      },
      user
    );

    setIncidentForm({
      type: 'Behavioural',
      description: '',
      date: new Date().toISOString().slice(0, 16),
      severity: 'medium',
      status: 'open',
      assignedKeyWorker: user?.name || '',
      followUpRequired: true,
      followUpDate: '',
      followUpPrompt: ''
    });
    setIncidentFiles([]);
    setIncidentInventorySelection([]);
    setIncidentStaffSelection([]);
    setIncidentError('');
    setShowIncidentModal(false);
  };

  const handleSafeguardingSubmit = () => {
    if (!safeguardingForm.type.trim() || !safeguardingForm.description.trim()) {
      setSafeguardingError('Type and description are required.');
      return;
    }
    const fileAttachments = eventFiles.map((file) => file.name);
    const textAttachments = safeguardingForm.attachments
      ? safeguardingForm.attachments.split(',').map((item) => item.trim()).filter(Boolean)
      : [];
    controller.addSafeguardingEvent(
      student.id,
      {
        category: 'Social Care',
        type: safeguardingForm.type.trim(),
        severity: safeguardingForm.severity,
        status: safeguardingForm.status,
        description: safeguardingForm.description.trim(),
        date: safeguardingForm.date,
        assignedOwner: safeguardingForm.assignedOwner,
        reviewDate: safeguardingForm.reviewDate || null,
        escalationLevel: safeguardingForm.escalationLevel,
        attachments: [...textAttachments, ...fileAttachments],
        createdBy: user?.name || 'System'
      },
      user
    );
    setSafeguardingForm({
      type: '',
      severity: 'medium',
      status: 'open',
      description: '',
      date: new Date().toISOString().slice(0, 10),
      assignedOwner: user?.name || '',
      reviewDate: '',
      escalationLevel: 'L1',
      attachments: ''
    });
    setEventFiles([]);
    setSafeguardingError('');
    setShowSafeguardingModal(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Avatar name={student.name} size="lg" color="slate" />
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>{student.name}</h2>
              <Badge variant={student.status === 'Under Review' ? 'review' : student.status === 'Archived' ? 'inactive' : 'active'}>
                {student.status}
              </Badge>
              {attendanceFlag && <Badge variant="danger">Attendance Flag</Badge>}
            </div>
            <div className="text-sm sp-muted">
              ID: {student.reference || student.id} - Age {student.age || 'N/A'} - {student.pronouns || 'Pronouns N/A'}
            </div>
            <div className="mt-2">
              <RiskIndicator level={student.riskLevel} />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => navigate('/students')}>
            Back to list
          </Button>
          <Button onClick={() => navigate(`/students/${student.id}/profile`)}>View full profile</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-5">
          <h3 className="sp-value" style={{ fontWeight: 600 }}>Vulnerability Overview</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {student.vulnerabilities.length ? (
              student.vulnerabilities.map((v) => (
                <Badge key={v} variant="info" className="sp-badge--subtle">
                  {v.replace('_', ' ')}
                </Badge>
              ))
            ) : (
              <span className="text-sm sp-muted">No vulnerabilities flagged.</span>
            )}
          </div>
        </Card>
        <Card className="p-5">
          <h3 className="sp-value" style={{ fontWeight: 600 }}>Active Safeguarding Alerts</h3>
          <p className="sp-value" style={{ fontSize: '28px', fontWeight: 600, marginTop: '12px' }}>{activeSafeguardingCount}</p>
          <p className="text-xs sp-muted">Open events requiring review</p>
        </Card>
        <Card className="p-5">
          <h3 className="sp-value" style={{ fontWeight: 600 }}>Assigned Key Worker</h3>
          <p className="text-sm sp-muted mt-3">{student.keyWorker?.name || 'Unassigned'}</p>
          <p className="text-xs sp-muted mt-1">{student.keyWorker?.notes || 'No notes recorded'}</p>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <AttendancePanel attendance={student.attendance} />
        </div>
        <Card className="p-5">
          <h3 className="sp-value" style={{ fontWeight: 600 }}>Quick Actions</h3>
          <div className="mt-4 space-y-2">
            <Button variant="secondary" className="w-full" onClick={() => setShowSafeguardingModal(true)}>
              Add safeguarding event
            </Button>
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => navigate(`/incidents/new?studentId=${student.id}`)}
            >
              Add incident report
            </Button>
            <Button variant="secondary" className="w-full" onClick={() => window.alert('Add internal note flow coming soon.')}
            >
              Add internal note
            </Button>
            <Button className="w-full" onClick={() => navigate(`/students/${student.id}/profile`)}>
              View full profile
            </Button>
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="space-y-4">
          <Card className="p-5">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Contacts</h3>
            <div className="mt-3 space-y-3">
              {student.contacts.length ? (
                student.contacts.map((contact) => (
                  <div key={contact.id} className="sp-panel sp-panel--muted p-3">
                    <p className="text-sm" style={{ fontWeight: 600 }}>{contact.name}</p>
                    <p className="text-xs sp-muted">{contact.role}</p>
                    <p className="text-xs sp-muted">{contact.phone} - {contact.email}</p>
                  </div>
                ))
              ) : (
                <p className="text-sm sp-muted">No contacts recorded.</p>
              )}
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Emergency Contacts</h3>
            <div className="mt-3 space-y-3">
              {emergencyContacts.length ? (
                emergencyContacts.map((contact) => (
                  <div key={contact.id} className="sp-panel sp-panel--muted p-3">
                    <p className="text-sm" style={{ fontWeight: 600 }}>{contact.name}</p>
                    <p className="text-xs sp-muted">{contact.role || 'Parent/Guardian'}</p>
                    <p className="text-xs sp-muted">{contact.phone || 'No phone'} {contact.email ? `· ${contact.email}` : ''}</p>
                  </div>
                ))
              ) : (
                <p className="text-sm sp-muted">No emergency contacts recorded.</p>
              )}
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between">
              <h3 className="sp-value" style={{ fontWeight: 600 }}>Incident Reports</h3>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => navigate(`/incidents/new?studentId=${student.id}`)}
              >
                Log incident
              </Button>
            </div>
            <div className="mt-4 space-y-3">
              {recentIncidents.length === 0 && (
                <p className="text-sm sp-muted">No incident reports logged.</p>
              )}
              {recentIncidents.map((incident) => (
                <div key={incident.id} className="sp-panel sp-panel--muted p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-sm" style={{ fontWeight: 600 }}>{incident.type}</p>
                      <p className="text-xs sp-muted">{formatDateTime(incident.date)} · {incident.assignedKeyWorker || 'Unassigned'}</p>
                    </div>
                    <Badge variant={incident.severity || 'medium'}>{incident.severity || 'medium'}</Badge>
                  </div>
                  {incident.followUpDate && (
                    <p className="text-xs sp-muted mt-2">Follow-up due {formatDate(incident.followUpDate)}</p>
                  )}
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Assigned Inventory</h3>
            <div className="mt-3 space-y-2">
              {assignedItems.length === 0 && <p className="text-sm sp-muted">No items assigned.</p>}
              {assignedItems.map(({ item, assignment, due }) => (
                <div key={`${item.id}-${assignment.id}`} className="sp-panel sp-panel--muted p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm" style={{ fontWeight: 600 }}>{item.name}</p>
                      <p className="text-xs sp-muted">Due {assignment.dueDate ? formatDate(assignment.dueDate) : 'N/A'}</p>
                    </div>
                    {due.isOverdue && <Badge variant="danger">Overdue</Badge>}
                    {!due.isOverdue && due.isDueSoon && <Badge variant="review">Due soon</Badge>}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between">
              <h3 className="sp-value" style={{ fontWeight: 600 }}>Documents & Certificates Due</h3>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => navigate(`/students/${student.id}/profile?tab=documents`)}
              >
                View
              </Button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              {documentAlerts.overdue > 0 && <Badge variant="danger">{documentAlerts.overdue} overdue</Badge>}
              {documentAlerts.dueSoon > 0 && <Badge variant="review">{documentAlerts.dueSoon} due soon</Badge>}
              {documentAlerts.expired > 0 && <Badge variant="danger">{documentAlerts.expired} expired</Badge>}
              {documentAlerts.expiringSoon > 0 && (
                <Badge variant="review">{documentAlerts.expiringSoon} expiring soon</Badge>
              )}
              {documentAlerts.overdue + documentAlerts.dueSoon + documentAlerts.expired + documentAlerts.expiringSoon === 0 && (
                <span className="text-sm sp-muted">No document deadlines currently flagged.</span>
              )}
            </div>
            {documentAlerts.alerts.length > 0 && (
              <div className="mt-3 space-y-2">
                {documentAlerts.alerts.slice(0, 4).map((alert) => (
                  <div key={`${alert.id}-${alert.type}`} className="sp-panel sp-panel--muted p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm" style={{ fontWeight: 600 }}>{alert.name}</p>
                        <p className="text-xs sp-muted">{alert.type} · {formatDate(alert.date) || 'TBD'}</p>
                      </div>
                      <Badge variant={alert.status === 'Overdue' || alert.status === 'Expired' ? 'danger' : 'review'}>
                        {alert.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div className="lg:col-span-2">
          <Card className="p-5">
            <div className="flex items-center justify-between">
              <h3 className="sp-value" style={{ fontWeight: 600 }}>Recent Events (Last 60 days)</h3>
            </div>
            <div className="mt-4">
              {timelineEvents.length ? (
                <TimelineView events={timelineEvents} />
              ) : (
                <p className="text-sm sp-muted">No recent events in the last 60 days.</p>
              )}
            </div>
          </Card>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-5">
          <div className="flex items-center justify-between">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Timetable & Lesson Plans</h3>
            <Button size="sm" variant="secondary" onClick={() => navigate('/programmes')}>
              View programmes
            </Button>
          </div>
          <div className="mt-3 space-y-2">
            {enrolledProgrammes.length === 0 && enrolledActivities.length === 0 && (
              <p className="text-sm sp-muted">No scheduled programmes or activities yet.</p>
            )}
            {enrolledProgrammes.map((programme) => (
              <div key={programme.id} className="sp-panel sp-panel--muted p-3">
                <div className="sp-value" style={{ fontWeight: 600 }}>{programme.name}</div>
                <div className="text-xs sp-muted">{programme.day || programme.days?.join(', ') || 'Schedule TBC'} · {programme.time || 'TBD'}</div>
                <div className="text-xs sp-muted">Lesson plans: {programme.lessonPlans?.length || 0}</div>
              </div>
            ))}
            {enrolledActivities.map((activity) => (
              <div key={activity.id} className="sp-panel sp-panel--muted p-3">
                <div className="sp-value" style={{ fontWeight: 600 }}>{activity.name}</div>
                <div className="text-xs sp-muted">{formatDate(activity.date)} · {activity.time || 'TBD'}</div>
                <div className="text-xs sp-muted">Lesson plans: {activity.lessonPlans?.length || 0}</div>
              </div>
            ))}
          </div>
          {lessonPlans.length > 0 && (
            <div className="mt-3">
              <div className="sp-label">Latest lesson plans</div>
              <div className="mt-2 space-y-2">
                {lessonPlans.slice(0, 3).map((plan) => (
                  <div key={plan.id} className="sp-panel sp-panel--muted p-3 text-xs">
                    <div className="sp-value" style={{ fontWeight: 600 }}>{plan.title || plan.fileName || 'Lesson plan'}</div>
                    <div className="text-xs sp-muted">{plan.source} · {plan.date ? formatDate(plan.date) : 'No date'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Assignments</h3>
            <Button size="sm" variant="secondary" onClick={() => navigate('/messages/external')}>
              View assignment hub
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge variant="success">Passed {assignmentStatusCounts.approved}</Badge>
            <Badge variant="review">Resubmit {assignmentStatusCounts.resubmit}</Badge>
            <Badge variant="danger">Failed {assignmentStatusCounts.failed}</Badge>
            <Badge variant="inactive">Pending {assignmentStatusCounts.pending}</Badge>
            {assignmentStatusCounts.other > 0 && (
              <Badge variant="inactive">Other {assignmentStatusCounts.other}</Badge>
            )}
          </div>
          <div className="mt-3 space-y-2">
            {assignments.length === 0 ? (
              <p className="text-sm sp-muted">No assignments assigned yet.</p>
            ) : (
              assignments.slice(0, 4).map((assignment) => (
                <div key={assignment.id} className="sp-panel sp-panel--muted p-3">
                  <div className="sp-value" style={{ fontWeight: 600 }}>{assignment.title}</div>
                  <div className="text-xs sp-muted">
                    Due {assignment.dueDate ? formatDate(assignment.dueDate) : 'No due date'}
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Student Portal</h3>
            <Button size="sm" variant="secondary" onClick={() => navigate('/messages/external')}>
              Open messages
            </Button>
          </div>
          <div className="mt-3 space-y-3 text-sm">
            <div className="sp-panel sp-panel--muted p-3">
              <div className="sp-value" style={{ fontWeight: 600 }}>Messages</div>
              <div className="text-xs sp-muted">{studentMessages.length} message{studentMessages.length === 1 ? '' : 's'} logged</div>
            </div>
            <div className="sp-panel sp-panel--muted p-3">
              <div className="sp-value" style={{ fontWeight: 600 }}>Support & wellbeing</div>
              <div className="text-xs sp-muted mt-2">
                <a className="sp-link" href="https://www.nhs.uk/mental-health/" target="_blank" rel="noreferrer">NHS mental health support</a>
              </div>
              <div className="text-xs sp-muted mt-1">
                <a className="sp-link" href="https://www.mind.org.uk/" target="_blank" rel="noreferrer">Mind charity</a>
              </div>
              <div className="text-xs sp-muted mt-1">
                <a className="sp-link" href="https://www.citizensadvice.org.uk/" target="_blank" rel="noreferrer">Citizens Advice Bureau</a>
              </div>
            </div>
            <div className="sp-panel sp-panel--muted p-3">
              <div className="sp-value" style={{ fontWeight: 600 }}>Wellbeing notes</div>
              <div className="text-xs sp-muted">Encourage check-ins, access to counselling, and support groups.</div>
            </div>
          </div>
        </Card>
      </div>

      <Modal isOpen={showSafeguardingModal} onClose={() => setShowSafeguardingModal(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Add Safeguarding Event</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowSafeguardingModal(false)} type="button">
              Close
            </Button>
          </div>
          {safeguardingError && (
            <div className="sp-alert sp-alert--critical text-sm">
              {safeguardingError}
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Type</label>
              <input
                value={safeguardingForm.type}
                onChange={(e) => setSafeguardingForm({ ...safeguardingForm, type: e.target.value })}
                className="sp-input mt-2 w-full"
                placeholder="e.g. Disclosure"
              />
            </div>
            <div>
              <label className="sp-label">Date</label>
              <DateSelect
                value={safeguardingForm.date}
                onChange={(value) => setSafeguardingForm({ ...safeguardingForm, date: value })}
                className="mt-2 w-full"
              />
            </div>
            <div>
              <label className="sp-label">Severity</label>
              <select
                value={safeguardingForm.severity}
                onChange={(e) => setSafeguardingForm({ ...safeguardingForm, severity: e.target.value })}
                className="sp-select mt-2 w-full"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="sp-label">Status</label>
              <select
                value={safeguardingForm.status}
                onChange={(e) => setSafeguardingForm({ ...safeguardingForm, status: e.target.value })}
                className="sp-select mt-2 w-full"
              >
                <option value="open">Open</option>
                <option value="monitoring">Monitoring</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label className="sp-label">Follow-up support worker</label>
              <SearchableSelect
                value={safeguardingForm.assignedOwner}
                options={[{ value: '', label: 'Unassigned' }, ...supportWorkerSelectOptions]}
                placeholder="Search staff"
                emptyLabel="No staff found."
                ariaLabel="Search support worker"
                className="mt-2"
                onChange={(value) => setSafeguardingForm({ ...safeguardingForm, assignedOwner: value })}
              />
            </div>
            <div>
              <label className="sp-label">Review / follow-up date</label>
              <DateSelect
                value={safeguardingForm.reviewDate}
                onChange={(value) => setSafeguardingForm({ ...safeguardingForm, reviewDate: value })}
                className="mt-2 w-full"
              />
            </div>
            <div>
              <label className="sp-label">Escalation level</label>
              <select
                value={safeguardingForm.escalationLevel}
                onChange={(e) => setSafeguardingForm({ ...safeguardingForm, escalationLevel: e.target.value })}
                className="sp-select mt-2 w-full"
              >
                <option value="L1">L1</option>
                <option value="L2">L2</option>
                <option value="L3">L3</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="sp-label">Description</label>
              <textarea
                value={safeguardingForm.description}
                onChange={(e) => setSafeguardingForm({ ...safeguardingForm, description: e.target.value })}
                rows={4}
                className="sp-textarea mt-2 w-full"
              />
            </div>
            <div className="md:col-span-2">
              <label className="sp-label">Attachments (comma-separated)</label>
              <input
                value={safeguardingForm.attachments}
                onChange={(e) => setSafeguardingForm({ ...safeguardingForm, attachments: e.target.value })}
                className="sp-input mt-2 w-full"
                placeholder="e.g. case-note.pdf, referral.docx"
              />
            </div>
            <div className="md:col-span-2">
              <label className="sp-label">Attach files</label>
              <input
                type="file"
                multiple
                className="sp-input mt-2 w-full"
                onChange={(e) => setEventFiles(Array.from(e.target.files || []))}
              />
              {eventFiles.length > 0 && (
                <div className="text-xs sp-muted mt-2">
                  {eventFiles.map((file) => file.name).join(', ')}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setShowSafeguardingModal(false)} type="button">
              Cancel
            </Button>
            <Button size="sm" onClick={handleSafeguardingSubmit}>
              Save Event
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showIncidentModal} onClose={() => setShowIncidentModal(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Log Incident Report</h3>
              <p className="text-xs sp-muted mt-1">Student: {student.name}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setShowIncidentModal(false)} type="button">
              Close
            </Button>
          </div>
          {incidentError && (
            <div className="sp-alert sp-alert--critical text-sm">
              {incidentError}
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Logged by</label>
              <input
                className="sp-input mt-2 w-full"
                value={user?.name || 'System'}
                readOnly
                aria-label="Logged by"
              />
            </div>
            <div>
              <label className="sp-label">Type</label>
              <select
                value={incidentForm.type}
                onChange={(e) => setIncidentForm({ ...incidentForm, type: e.target.value })}
                className="sp-select mt-2 w-full"
              >
                {incidentTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="sp-label">Date & time</label>
              <input
                type="datetime-local"
                value={incidentForm.date}
                onChange={(e) => setIncidentForm({ ...incidentForm, date: e.target.value })}
                className="sp-input mt-2 w-full"
              />
            </div>
            <div>
              <label className="sp-label">Severity</label>
              <select
                value={incidentForm.severity}
                onChange={(e) => setIncidentForm({ ...incidentForm, severity: e.target.value })}
                className="sp-select mt-2 w-full"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="sp-label">Status</label>
              <select
                value={incidentForm.status}
                onChange={(e) => setIncidentForm({ ...incidentForm, status: e.target.value })}
                className="sp-select mt-2 w-full"
              >
                <option value="open">Open</option>
                <option value="monitoring">Monitoring</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label className="sp-label">Assigned key worker</label>
              <SearchableSelect
                value={incidentForm.assignedKeyWorker}
                options={[{ value: '', label: 'Unassigned' }, ...supportWorkerSelectOptions]}
                placeholder="Search staff"
                emptyLabel="No staff found."
                ariaLabel="Search key worker"
                className="mt-2"
                onChange={(value) => setIncidentForm({ ...incidentForm, assignedKeyWorker: value })}
              />
            </div>
            <div>
              <label className="sp-label">Follow-up date</label>
              <DateSelect
                value={incidentForm.followUpDate}
                onChange={(value) =>
                  setIncidentForm({ ...incidentForm, followUpDate: value, followUpRequired: true })
                }
                className="mt-2 w-full"
              />
            </div>
            <div className="md:col-span-2">
              <label className="sp-label">Description</label>
              <textarea
                value={incidentForm.description}
                onChange={(e) => setIncidentForm({ ...incidentForm, description: e.target.value })}
                rows={4}
                className="sp-textarea mt-2 w-full"
              />
            </div>
            <div className="md:col-span-2">
              <label className="sp-label">Follow-up prompt</label>
              <textarea
                value={incidentForm.followUpPrompt}
                onChange={(e) => setIncidentForm({ ...incidentForm, followUpPrompt: e.target.value })}
                rows={3}
                className="sp-textarea mt-2 w-full"
                placeholder="Next steps / follow-up instructions"
              />
            </div>
            <div className="md:col-span-2">
              <label className="sp-label">Co-workers / staff involved</label>
              <SearchableChecklist
                className="mt-2"
                items={staffList}
                selectedIds={incidentStaffSelection}
                onToggle={toggleIncidentStaff}
                placeholder="Search staff"
                getLabel={(worker) => worker.name}
                getMeta={(worker) => worker.role || worker.title || ''}
                emptyLabel="No staff found."
              />
            </div>
            <div className="md:col-span-2">
              <div className="flex items-center justify-between">
                <label className="sp-label">Related inventory</label>
                <Button size="sm" variant="ghost" type="button" onClick={() => setShowInventoryModal(true)}>
                  Add item
                </Button>
              </div>
              {inventoryItems.length > 0 ? (
                <SearchableChecklist
                  className="mt-2"
                  items={inventoryItems}
                  selectedIds={incidentInventorySelection}
                  onToggle={toggleIncidentInventory}
                  placeholder="Search inventory"
                  getLabel={(item) => item.name}
                  getMeta={(item) => item.reference || item.type}
                  emptyLabel="No inventory items match."
                />
              ) : (
                <p className="text-sm sp-muted mt-2">No inventory items available.</p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="sp-label">Attachments</label>
              <input
                type="file"
                multiple
                className="sp-input mt-2 w-full"
                onChange={(e) => setIncidentFiles(Array.from(e.target.files || []))}
              />
              {incidentFiles.length > 0 && (
                <div className="text-xs sp-muted mt-2">
                  {incidentFiles.map((file) => file.name).join(', ')}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setShowIncidentModal(false)} type="button">
              Cancel
            </Button>
            <Button size="sm" onClick={handleIncidentSubmit}>
              Save Incident
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showInventoryModal} onClose={() => setShowInventoryModal(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Add Inventory Item</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowInventoryModal(false)} type="button">
              Close
            </Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="sp-label">Item name</label>
              <input
                value={newInventoryItem.name}
                onChange={(e) => setNewInventoryItem({ ...newInventoryItem, name: e.target.value })}
                className="sp-input mt-2 w-full"
                placeholder="Item name"
              />
            </div>
            <div>
              <label className="sp-label">Type</label>
              <input
                value={newInventoryItem.type}
                onChange={(e) => setNewInventoryItem({ ...newInventoryItem, type: e.target.value })}
                className="sp-input mt-2 w-full"
                placeholder="Type"
              />
            </div>
            <div>
              <label className="sp-label">Condition</label>
              <input
                value={newInventoryItem.condition}
                onChange={(e) => setNewInventoryItem({ ...newInventoryItem, condition: e.target.value })}
                className="sp-input mt-2 w-full"
                placeholder="Condition"
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setShowInventoryModal(false)} type="button">
              Cancel
            </Button>
            <Button size="sm" onClick={handleAddInventoryItem}>
              Add Item
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
